import React, { memo } from 'react';
import type { CircleProgressBarProps } from '../../types';
import { DEFAULT_FONT_FALLBACK_CHAINS } from '../../../../../utils/styleMapper';
import styles from './CircleProgressBar.module.css';

export const CircleProgressBar = memo(function CircleProgressBar({
  progress,
  appearance,
  labelStyle,
  hasLabelStyle,
  className,
  testId,
}: CircleProgressBarProps): React.ReactElement {
  const viewBoxSize = 150;
  const strokeWidth = appearance?.thickness ?? 6;
  const trackStrokeWidth = appearance?.trackThickness ?? 2;
  const center = viewBoxSize / 2;
  const radius = center - Math.max(strokeWidth, trackStrokeWidth);
  const circumference = 2 * Math.PI * radius;

  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Font size from label style or default
  const fontSize = hasLabelStyle && labelStyle.fontSize
    ? (typeof labelStyle.fontSize === 'number' ? labelStyle.fontSize * 0.6 : 24)
    : 24;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
      style={{ aspectRatio: '1 / 1', maxHeight: '100%' }}
      className={className}
      data-testid={testId}
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={appearance?.trackColor ?? '#ededed'}
        strokeWidth={trackStrokeWidth}
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={appearance?.fillColor ?? appearance?.color ?? '#12777f'}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
        className={styles.circleFill}
      />
      <text
        x={center}
        y={center}
        dominantBaseline="central"
        textAnchor="middle"
        fontFamily={hasLabelStyle && labelStyle.fontFamily ? String(labelStyle.fontFamily) : DEFAULT_FONT_FALLBACK_CHAINS['SF Pro']}
        fontSize={fontSize}
        fontWeight={hasLabelStyle && labelStyle.fontWeight ? labelStyle.fontWeight : 700}
        fill={hasLabelStyle && labelStyle.color ? String(labelStyle.color) : '#000000'}
      >
        {Math.round(progress)}%
      </text>
    </svg>
  );
});

CircleProgressBar.displayName = 'CircleProgressBar';
